/**
 * The AI-assistant OPERATION vocabulary (`docs/V2_AI_ASSISTANT.md` §4, §9 build-order step 1).
 *
 * > The model never emits a rider. It emits operations, and the server applies them. (§2)
 *
 * This is Phase 1 of §4's table — placement — plus `place_relative`, the doc's biggest hallucination
 * reducer. It is part of the shared contract (client, server, any future mobile client), versioned
 * like the rider schema (`SCHEMA_CONTRACT_VERSION` in `index.ts`), and — like `schema.ts` — the
 * single hand-maintained source that `docs/schemas/ops.schema.json` is generated from.
 *
 * Structural shape rules, straight from §4's "Adapter note (Gemini)": each op's OWN argument object
 * is a flat record of primitives/enums — no nested objects, because Gemini's function-calling schema
 * support is an OpenAPI subset with no documented `oneOf`/deep-nesting guarantee, and the eventual
 * Gemini adapter (§9 step 2, NOT built here) flattens this discriminated union into one tool
 * declaration per operation. The ONE deliberate exception is `place_relative`'s `anchor` field
 * (`Uuid | NamedStageAnchor`) — still a single scalar-ish value at the wire level (one string), but
 * a zod union of two string shapes rather than a single enum/pattern. Flagged here rather than
 * silently diverging from the "free of unions" guidance: the Gemini adapter will need to model this
 * as e.g. two separate optional string args (`anchor_uuid?`, `anchor_name?`) when it flattens this
 * operation into a tool declaration — that translation is out of scope for this step (no adapter
 * exists yet) but the seam is exactly this file.
 *
 * This module has ZERO knowledge of `apps/web/src/editor/rider.ts` — it only defines and validates
 * the WIRE shape. Turning a validated `Op` into a rider mutation is `apps/web/src/editor/ai-ops.ts`
 * (semantic validation + apply-to-clone + diff), per §3 of the doc.
 */
import { z } from "zod";
import { UuidSchema } from "./schema";
// -------------------------------------------------------------------------------------------
// The discriminant — exported both as the zod enum AND as a plain readonly array, so a provider
// adapter can iterate it 1:1 when generating "one tool per operation" (§4 adapter note) without
// reaching into zod internals.
// -------------------------------------------------------------------------------------------
export const OP_TYPES = [
    "add_preset",
    "move_object",
    "rotate_object",
    "delete_object",
    "set_rider_meta",
    "place_relative",
];
export const OpTypeSchema = z.enum(OP_TYPES);
// -------------------------------------------------------------------------------------------
// Phase 1 — placement (§4 table 1: "this is v1's working subset, made safe").
// -------------------------------------------------------------------------------------------
/**
 * Instantiate a catalogue preset onto the stage (or as a child of an existing node). Applied via
 * `insertPresetAsChild` — new uuids, §3b channel allocation, collision nudge, stage clamp, all come
 * free from that primitive; this op only ever carries a `preset_id` (validated against the real
 * catalogue server/client-side — an unknown id REJECTS the operation, §4/§5, never a dangling `src`)
 * plus the placement the model wants.
 *
 * `turn` here is the ABSOLUTE orientation of the newly-placed object (not a delta) — there is no
 * "current turn" for a node that doesn't exist yet.
 */
export const AddPresetOpSchema = z
    .object({
    op: z.literal("add_preset"),
    preset_id: z.string().min(1).max(200),
    x: z.number().finite(),
    y: z.number().finite(),
    turn: z.number().finite(),
    parent_uuid: UuidSchema.optional(),
})
    .strict();
/** Move an existing node to an absolute (x, y) — `setNodePosition`. Z/turn untouched. */
export const MoveObjectOpSchema = z
    .object({
    op: z.literal("move_object"),
    uuid: UuidSchema,
    x: z.number().finite(),
    y: z.number().finite(),
})
    .strict();
/**
 * Rotate an existing node — `rotateNode` (MODE 1: this one node only, its children counter-rotated
 * to stay visually put — §4's "keeps child compensation"). `turn` is a DELTA in degrees (positive or
 * negative), matching `rotateNode`'s own signature — "turn it 90 degrees" is a delta, not a target.
 */
export const RotateObjectOpSchema = z
    .object({
    op: z.literal("rotate_object"),
    uuid: UuidSchema,
    turn: z.number().finite(),
})
    .strict();
/**
 * Delete an existing node. `with_children` picks `removeNodeByUuid` (whole subtree) vs.
 * `removeNodeKeepChildren` (children promoted, keep their world pose) — §4: "the I/O keep/optimize
 * dialog still fires for the human." The apply pipeline (`ai-ops.ts`) always converts the removed
 * node's own audio-list lines to root-owned `Unconnected` placeholders (the "Keep" policy) rather
 * than optimizing/renumbering — the safe, non-destructive default for a headless apply with nobody
 * to ask; a future UI can offer "Optimize I/O" as a follow-up action on the same diff.
 */
export const DeleteObjectOpSchema = z
    .object({
    op: z.literal("delete_object"),
    uuid: UuidSchema,
    with_children: z.boolean(),
})
    .strict();
/** Edit root-level rider metadata. Every field optional — the model sets only what the user asked
 *  to change. `width`/`length` are meters and must be positive (mirrors the "Rider" tab's own
 *  `value > 0` guard, `components/rider-tab.ts`). */
export const SetRiderMetaOpSchema = z
    .object({
    op: z.literal("set_rider_meta"),
    title: z.string().max(255).optional(),
    comment: z.string().max(5000).optional(),
    width: z.number().finite().positive().optional(),
    length: z.number().finite().positive().optional(),
})
    .strict();
// -------------------------------------------------------------------------------------------
// `place_relative` — §4 "the single biggest hallucination reducer." The model reasons in the words
// a sound engineer actually uses; the server (ai-ops.ts) does the trigonometry.
// -------------------------------------------------------------------------------------------
/**
 * Named points on the stage rectangle, independent of any object. `stage_centre` is always the
 * world origin (the root is permanently fixed there, `V2_ARCHITECTURE.md` "Coordinate & stage
 * convention"); the other six are the four corners plus the two edge midpoints along the
 * upstage/downstage axis. See `ai-ops.ts` for the concrete axis convention this resolves against —
 * CORRECTED 2026-07-30 against what the editor camera actually renders: **X = DEPTH** (+X =
 * downstage/front toward the audience, -X = upstage/back), **Y = LEFT-RIGHT**. The earlier note
 * here had X/Y the other way round, which made every AI placement come out rotated 90° (owner
 * visual report).
 *
 * THE `*_left` / `*_right` IN THESE NAMES ARE THE PERFORMER'S (stage frame): **-Y = stage right, +Y =
 * stage left** (SCRUM-158, corrected 2026-08-31 — canon in `RIDER_SCHEMA_SPEC.md` §2.1.1a). The same
 * -Y side is HOUSE left, and since SCRUM-537 (2026-10-04) a user's plain "left" means house left — so
 * the house-left corners are the `*_right` anchors. The names stay as they are: renaming would change
 * this contract. This comment said "+Y = stage right on screen" until 2026-08-31, describing the
 * VIEWER's right, which is house right and a different thing.
 * That reading survived here after the code was fixed, which is the whole hazard: a stale comment
 * in the contract package is where the next reader learns the convention, so it teaches the error
 * long after the behaviour stopped exhibiting it.
 */
export const NAMED_STAGE_ANCHORS = [
    "stage_centre",
    "downstage_centre",
    "downstage_left",
    "downstage_right",
    "upstage_centre",
    "upstage_left",
    "upstage_right",
];
export const NamedStageAnchorSchema = z.enum(NAMED_STAGE_ANCHORS);
/** The four cardinal stage directions a sound engineer actually says out loud. Same axis convention
 *  as `NamedStageAnchor` above. */
export const PLACE_RELATIVE_DIRECTIONS = ["upstage", "downstage", "stage_left", "stage_right"];
export const PlaceRelativeDirectionSchema = z.enum(PLACE_RELATIVE_DIRECTIONS);
/** A uuid (an existing node — its WORLD position, computed by `ai-ops.ts` via
 *  `structure.ts#worldTransformOf`, not its raw parent-relative X/Y) or one of the named points above. */
export const PlaceRelativeAnchorSchema = z.union([UuidSchema, NamedStageAnchorSchema]);
export const PlaceRelativeOpSchema = z
    .object({
    op: z.literal("place_relative"),
    preset_id: z.string().min(1).max(200),
    anchor: PlaceRelativeAnchorSchema,
    direction: PlaceRelativeDirectionSchema,
    distance_m: z.number().finite().min(0).max(1000),
})
    .strict();
// -------------------------------------------------------------------------------------------
// The union, validator, and batch cap.
// -------------------------------------------------------------------------------------------
export const OpSchema = z.discriminatedUnion("op", [
    AddPresetOpSchema,
    MoveObjectOpSchema,
    RotateObjectOpSchema,
    DeleteObjectOpSchema,
    SetRiderMetaOpSchema,
    PlaceRelativeOpSchema,
]);
/**
 * Cap on ops per batch — defence in depth alongside §6.2's max-output-tokens bound, independent of
 * provider: a runaway/looping generation should fail fast here rather than produce a huge apply.
 * No real hand-authored request comes close to this; it exists purely as a sanity ceiling.
 */
export const MAX_OPS_PER_BATCH = 50;
export const OpBatchSchema = z.array(OpSchema).min(1).max(MAX_OPS_PER_BATCH);
/**
 * Structural validation entry point, mirroring `validateRider()`'s shape exactly (`validate.ts`) —
 * same never-throws contract, same `{ success, data|errors }` result. Accepts a raw JSON string
 * (as a provider's tool-call arguments might arrive) or an already-parsed value (an array of op
 * objects). This is ONLY the zod/structural layer (§3 pipeline step [1]) — semantic checks
 * (preset_id/uuid resolution, stage bounds — step [2]) are `apps/web/src/editor/ai-ops.ts`'s job,
 * because they need a live rider + preset catalogue this package doesn't have access to.
 */
export function validateOps(input) {
    let candidate = input;
    if (typeof input === "string") {
        try {
            candidate = JSON.parse(input);
        }
        catch (err) {
            return { success: false, errors: [{ path: "$", message: `malformed JSON: ${err.message}` }] };
        }
    }
    const parsed = OpBatchSchema.safeParse(candidate);
    if (!parsed.success) {
        const errors = parsed.error.issues.map((issue) => ({
            path: issue.path.join("."),
            message: issue.message,
        }));
        return { success: false, errors };
    }
    return { success: true, data: parsed.data, warnings: [] };
}
//# sourceMappingURL=ops.js.map